import React, { useCallback, useMemo } from 'react';
import type { ViewProps } from 'react-native';
import { ProductHeader } from '@sweetgreen/garnish';

import { useLocalizationContext } from '@order/Localization';

import { useProductDetailsScreenContext } from '../../../../ProductDetailsScreen.provider';

export const ProductDetailsHeader = (props: ProductDetailsHeaderProps) => {
  const { t, formatPrice, formatNumber } = useLocalizationContext();
  const {
    customizeLineItemName,
    isModifiableProduct,
    modifications,
    product,
    quantity,
  } = useProductDetailsScreenContext();

  const { calories, netPriceChange, customName } = modifications;
  const cost = product?.cost ?? 0;

  const price = useMemo(() => {
    const formattedPrice = formatPrice(cost + netPriceChange, 'USD');

    if (quantity > 1) return `${formattedPrice}/${t('pdp.modifications.each')}`;

    return formattedPrice;
  }, [cost, quantity, t, formatPrice, netPriceChange]);

  const formattedCalories = useMemo(() => {
    const formattedCaloriesWithText = `${formatNumber(calories)} ${t(
      'general.calories-short',
    )}`;

    if (quantity > 1)
      return `${formattedCaloriesWithText}/${t('pdp.modifications.each')}`;

    return formattedCaloriesWithText;
  }, [calories, quantity, t, formatNumber]);

  const handleFinishEditing = useCallback(
    (text) => {
      customizeLineItemName(text);
    },
    [customizeLineItemName],
  );

  // ─────────────────────────────────────────────────────────────────

  return (
    <ProductHeader
      value={customName}
      price={price}
      calories={formattedCalories}
      isModifiableProduct={isModifiableProduct}
      textInputAccessibilityLabel={t('pdp.details.customize-line-item-name')}
      style={props.style}
      onFinishEditing={handleFinishEditing}
    />
  );
};

// ─── TYPES ──────────────────────────────────────────────────────────────────────

type ProductDetailsHeaderProps = Pick<ViewProps, 'style'>;
